import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocationState } from '../../../../Common/Util/CustomHooks';
import * as request from '../../../../Common/Util/Request';
import * as Button from '../../../../Common/Component/Button';
import Table from '../../../../Common/Component/Table';
import './index.scss';
import Chip from '../../../../Common/Component/Chip';
import * as excel from '../../../../Common/Util/Excel';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { createTheme, Input, MenuItem, Select, ThemeProvider } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';

function LectureWaitingList(props) {
  const [locationState, setLocationState] = useLocationState({
    startDate: null,
    endDate: null,
    searchOption: 'title',
    searchText: '',
    filters: [],
    selectedStatuses: [],
    selectedStatusType: 'waiting',
  });
  const { selectedStatuses, selectedStatusType, startDate, endDate, filters } = locationState;
  const [searchOption, setSearchOption] = useState(locationState.searchOption);
  const [searchText, setSearchText] = useState(locationState.searchText);
  const [waitings, swtWaitings] = useState([]);
  const [total, setTotal] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  /** status 설정 */
  const statusTypes = useMemo(() => ['waiting'], []);

  const statuses = useMemo(() => {
    switch (selectedStatusType) {
      case 'waiting':
        return ['안내 예정', '안내 가능', '안내 종료'];
      default:
        return [];
    }
  }, [selectedStatusType]);

  const onClickClear = useCallback(() => {
    setLocationState({ selectedStatuses: [] });
  }, [setLocationState]);

  // status 유형 많아질 경우 사용
  const onClickStatusType = useCallback(
    (type) => {
      setLocationState({
        selectedStatusType: type,
        selectedStatuses: [],
      });
    },
    [setLocationState],
  );

  const onClickStatus = useCallback(
    (status) => {
      const index = selectedStatuses.indexOf(status);
      const _selectedStatuses = [...selectedStatuses];
      if (index >= 0) {
        _selectedStatuses.splice(index, 1);
      } else {
        _selectedStatuses.push(status);
      }
      setLocationState({ selectedStatuses: _selectedStatuses });
    },
    [selectedStatuses, setLocationState],
  );

  const statusInfo = useMemo(() => {
    const info = { waiting: [] };
    info[selectedStatusType] = selectedStatuses.map((status) => statuses.indexOf(status) + 1);
    return info;
  }, [statuses, selectedStatusType, selectedStatuses]);

  /** 검색 설정 */
  const onClickSearch = useCallback(() => {
    setLocationState({
      searchOption,
      searchText: searchOption === 'phone' ? searchText.replace(/^0/, '82') : searchText,
      filters: searchText ? [{ id: searchOption, value: searchText }] : [],
    });
  }, [setLocationState, searchOption, searchText]);

  /** 테이블 설정 */
  const loadWaitings = useCallback(
    ({ page, limit }) => {
      const params = {
        page,
        limit,
        filtered: JSON.stringify(filters),
        status_info: JSON.stringify(statusInfo),
      };

      if (startDate) {
        params.start_date = dayjs(startDate).format('YYYY-MM-DD');
      }
      if (endDate) {
        params.end_date = dayjs(endDate).format('YYYY-MM-DD');
      }

      return request.lectureWaitings(params).then(({ data }) => data);
    },
    [startDate, endDate, filters, statusInfo],
  );

  const fetchData = useCallback(
    ({ pageIndex, pageSize }) => {
      loadWaitings({ page: pageIndex + 1, limit: pageSize }).then((classWaitings) => {
        swtWaitings(classWaitings.rows);
        setTotal(classWaitings.count);
      });
    },
    [loadWaitings],
  );

  const columns = useMemo(
    () => [
      { Header: '대기번호', accessor: 'id' },
      {
        Header: '대기 상태',
        accessor: 'status',
        Cell: ({ value: status }) => (status === 0 ? '안내 종료' : status === 1 ? '안내 예정(오픈 대기)' : '안내 가능'),
      },
      { Header: '강의ID', accessor: 'FK_class.id' },
      { Header: '강의명', accessor: 'FK_class.title' },
      {
        Header: '구분',
        accessor: 'FK_class.listen_type',
        Cell: ({ value: listen_type }) => <FormattedMessage id={'ID_LISTEN_TYPE_' + listen_type.toUpperCase()} />,
      },
      { Header: '회원ID', accessor: 'FK_user.id' },
      { Header: '이름', accessor: 'FK_user.name' },
      { Header: '닉네임', accessor: 'FK_user.nickname' },
      {
        Header: '핸드폰',
        accessor: 'FK_user.phone',
        Cell: ({ value: phone }) => phone?.replace(/^82/, '0') ?? '',
      },
      { Header: '이메일', accessor: 'FK_user.email' },
      {
        Header: '대기 신청일',
        accessor: 'createdAt',
        Cell: ({ value: createdAt }) => (createdAt ? dayjs(createdAt).format('YYYY-MM-DD') : '-'),
      },
      {
        Header: '결제일',
        accessor: 'paidAt',
        Cell: ({ value: paidAt }) => (paidAt ? dayjs.utc(paidAt).format('YYYY-MM-DD') : '-'),
      },
      {
        Header: '안내 횟수',
        accessor: 'messageLogs',
        Cell: ({ value: messageLogs }) => (messageLogs?.length ? messageLogs.length : 0),
      },
    ],
    [],
  );

  const onClickCSV = useCallback(() => {
    const startDateDayjs = startDate ? dayjs(startDate) : dayjs('2020-01-01');
    const endDateDayjs = endDate ? dayjs(endDate) : dayjs();
    if (endDateDayjs.diff(startDateDayjs, 'day') > 365) {
      Swal.fire({
        title: '기간 설정 오류',
        text: '다운로드 가능한 기간은 최대 1년입니다. 기간을 다시 설정해주세요.',
        icon: 'error',
      });
      return;
    }

    setIsDownloading(true);
    loadWaitings({ page: 1, limit: 9999999 })
      .then((waitings) => {
        const csvData = waitings.rows.map((waiting) => ({
          대기번호: waiting.id,
          대기상태: waiting.status === 0 ? '안내 종료' : waiting.status === 1 ? '안내 예정(오픈 대기)' : '안내 가능',
          강의ID: waiting.FK_class.id,
          강의명: waiting.FK_class.title,
          구분: <FormattedMessage id={'ID_LISTEN_TYPE_' + waiting.FK_class.listen_type.toUpperCase()} />,
          회원ID: waiting.FK_user.id,
          이름: waiting.FK_user.name,
          닉네임: waiting.FK_user.nickname,
          핸드폰: waiting.FK_user.phone?.replace(/^82/, '0') ?? '',
          이메일: waiting.FK_user.email,
          '대기 신청일': waiting.createdAt ? dayjs(waiting.createdAt).format('YYYY-MM-DD') : '-',
          결제일: waiting.paidAt ? dayjs.utc(waiting.paidAt).format('YYYY-MM-DD') : '-',
          '안내 횟수': waiting.messageLogs?.length ? waiting.messageLogs.length : 0,
        }));
        excel.downloadXLSX(csvData, `강의대기목록_${dayjs.utc().add(9, 'h').format('YYYYMMDD')}`);
        setIsDownloading(false);
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: '다운로드 실패',
          text: '다운로드에 실패하였습니다. 다시 시도해주세요.',
        });
        setIsDownloading(false);
      });
  }, [loadWaitings, startDate, endDate]);

  const ejeTheme = createTheme({
    palette: {
      pink: {
        light: '#f46981',
        main: '#F24462',
        dark: '#a92f44',
        contrastText: '#fff',
      },
    },
  });

  return (
    <div className="lecture-waiting-list">
      <ThemeProvider theme={ejeTheme}>
        <div className="lecture-waiting-list__top">
          <div className="lecture-waiting-list__date">
            기간
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={startDate && dayjs(startDate)}
                maxDate={endDate && dayjs(endDate)}
                onChange={(value) => setLocationState({ startDate: dayjs(value).format('YYYY-MM-DD') })}
                label={startDate && 'From'}
                disableFuture
                views={['year', 'month', 'day']}
              />
              -
              <DatePicker
                value={endDate && dayjs(endDate)}
                minDate={startDate && dayjs(startDate)}
                onChange={(value) => setLocationState({ endDate: dayjs(value).format('YYYY-MM-DD') })}
                label={endDate && 'From'}
                disableFuture
                views={['year', 'month', 'day']}
              />
            </LocalizationProvider>
          </div>
          <div className="lecture-waiting-list__search">
            <Select
              value={searchOption}
              onChange={(e) => setSearchOption(e.target.value)}
              displayEmpty
              color="pink"
              size="small"
            >
              <MenuItem value="username" sx={{ fontSize: '0.875rem' }}>
                회원 이름
              </MenuItem>
              <MenuItem value="nickname" sx={{ fontSize: '0.875rem' }}>
                닉네임
              </MenuItem>
              <MenuItem value="email" sx={{ fontSize: '0.875rem' }}>
                이메일
              </MenuItem>
              <MenuItem value="phone" sx={{ fontSize: '0.875rem' }}>
                핸드폰번호
              </MenuItem>
              <MenuItem value="FK_user_id" sx={{ fontSize: '0.875rem' }}>
                회원ID
              </MenuItem>
              <MenuItem value="FK_class_id" sx={{ fontSize: '0.875rem' }}>
                강의ID
              </MenuItem>
              <MenuItem value="title" sx={{ fontSize: '0.875rem' }}>
                강의명
              </MenuItem>
              <MenuItem value="name" sx={{ fontSize: '0.875rem' }}>
                강사명
              </MenuItem>
            </Select>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onClickSearch();
              }}
              color="pink"
              placeholder="검색어를 입력하세요."
            />
            <Button.MUI
              onClick={onClickSearch}
              variant="outlined"
              endIcon={<SearchIcon fontSize="small" />}
              color="pink"
              sx={{ height: '2rem' }}
            >
              검색
            </Button.MUI>
          </div>
        </div>
        <div className="waiting-list__status">
          <div className="waiting-list__status__types">
            {statusTypes.map((type) => (
              <div key={type} className={'waiting-list__status__type' + ' waiting-list__status__type--selected'}>
                <FormattedMessage id={'ID_WAITING_TYPE_' + type.toUpperCase()} />
              </div>
            ))}
          </div>
          <div className="waiting-list__status__statuses">
            {statuses.map((status) => (
              <Chip key={status} name={status} onClick={() => onClickStatus(status)} />
            ))}
          </div>
          <div className="waiting-list__status__selected-statuses">
            {selectedStatuses.map((status) => (
              <Chip key={status} name={status} onClick={() => onClickStatus(status)} selected />
            ))}
            {selectedStatuses.length > 0 && (
              <div className="waiting-list__status__clear" onClick={onClickClear}>
                초기화
              </div>
            )}
          </div>
        </div>
        <div className="lecture-waiting-list__total">
          총 신청 회원수 {total}
          <button
            className={
              'lecture-waiting-list__excel--btn' +
              (filters.length < 1 && !startDate && !endDate ? ' lecture-waiting-list__excel__disabled--btn' : '')
            }
            style={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
            onClick={() => {
              filters.length < 1 && !startDate && !endDate
                ? Swal.fire({
                    title: '기간 설정 또는 검색 후 다운로드 가능합니다.',
                    text: '좌측 상단에서 기간 설정 또는 검색 후 다운로드해 주세요.',
                    icon: 'info',
                    confirmButtonColor: '#F24462',
                  })
                : !isDownloading
                ? onClickCSV()
                : undefined;
            }}
          >
            {isDownloading ? 'Loading...' : '액셀 다운로드'}
          </button>
        </div>
        <Table data={waitings} columns={columns} total={total} pageSize={20} fetchData={fetchData} />
      </ThemeProvider>
    </div>
  );
}

export default LectureWaitingList;
